import React, { Suspense, useState, useMemo, useRef, useCallback, useEffect } from "react";
import { Await, useRouteLoaderData, useLoaderData } from "react-router-dom";
import { Col, Form, ToggleButton, ToggleButtonGroup, Stack, Button } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import PageDesciption from "../../components/PageDesciption";
import PlaylistAutopilotHeader from "../../components/playlistAutopilot/PlaylistAutopilotHeader";
import PlaylistAutopilotRow from "../../components/playlistAutopilot/PlaylistAutopilotRow";
import PlaylistAutopilotImportModal from "../../components/playlistAutopilot/PlaylistAutopilotImportModal";
import { useUserFormState } from "../../components/UserSelection";
import {
    DataContent,
    InputSelection,
    ModulePage,
    SearchWithDownloadRow,
    SoleDescriptionRow,
    VirtualizedWindowContainer,
} from "../../components/pageSelections";
import { mapPlaylistPerspective } from "../../components/HelperFunctions";
import {
    useFetchData,
    useStationsAvailableInModule,
    useSetCategoriesOnPlaylistPerspectiveChange,
    useSetPlaylistPerspectiveOnCategoriesChange,
    useSongCategoriesWithPerspectives,
    getRadioName,
} from "../../components/HelperHooks";
import { useMusicTestTrendModal } from "../../components/TrendOverTime";
import { axiosInstance } from "../../auth/Auth";
import useSortableData from "../../components/useSortableData";
import moment from "moment/min/moment-with-locales";
import RATooltip from "../../components/RATooltip";
import EmptyTableRow from "../../components/EmptyTableRow";
import { Upload } from "react-bootstrap-icons";
import StationPicker from "../../components/Pickers/StationPicker";
import SongCategoriesPicker from "../../components/Pickers/SongCategoriesPicker";

const PlaylistAutopilot = () => {
    const moduleName = "playlistAutopilot";
    const { customPlaylistAutopilotRadios } = useLoaderData();
    const [formData, setFormData, saveFormData, loadFormData] = useUserFormState(moduleName);
    const [loading, errorCode, data, fetchData, setData] = useFetchData();
    const { radios, competitorRadios, radiosModuleSettings } = useRouteLoaderData("root");
    const [filterData, setFilterData] = useState({
        playlistPerspective: [],
        categories: [],
        showMoves: "changes",
        searchString: "",
    });
    const [customResults, setCustomResults] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);

    const stationsAvailable = useStationsAvailableInModule(x => x.module_id === 15);
    const categoriesStation = useMemo(() => (customResults ? data?.radio_id : null), [customResults, data?.radio_id]);
    const perspectivesWithCategories = useSongCategoriesWithPerspectives(categoriesStation);

    const tableContainerRef = useRef(null);

    const [demos, setDemos] = useState(1);
    const { setTrendSong, MusicTestTrendModalComponent } = useMusicTestTrendModal();

    useSetCategoriesOnPlaylistPerspectiveChange(setFilterData, filterData.playlistPerspective, perspectivesWithCategories);
    useSetPlaylistPerspectiveOnCategoriesChange(setFilterData, filterData.categories, perspectivesWithCategories);

    const dataMemo = useMemo(() => {
        if (data) {
            let filteredData = data.data;
            if (filterData.showMoves === "changes") {
                filteredData = filteredData?.filter(item => item.song_category !== item.song_category_move);
            }
            if (filterData.categories.length) {
                filteredData = filteredData.filter(x => filterData.categories.some(c => x.song_category === c.code || x.song_category_move === c.code));
            }
            if (filterData.searchString) {
                filteredData = filteredData.filter(
                    x =>
                        x.song.artist.toLowerCase().includes(filterData.searchString.toLowerCase().trim()) ||
                        x.song.title.toLowerCase().includes(filterData.searchString.toLowerCase().trim()) ||
                        x.song.release_year?.toString().includes(filterData.searchString.trim()),
                );
            }
            return filteredData;
        }
    }, [data, filterData]);

    useEffect(() => {
        if (perspectivesWithCategories?.length) {
            setFilterData(current => ({
                ...current,
                playlistPerspective: JSON.stringify(perspectivesWithCategories[0].categories.map(x => x.code)),
            }));
        }
    }, [perspectivesWithCategories]);

    const { sortedItems, requestSort, sortConfig } = useSortableData(dataMemo, { key: "spins", direction: "descending" });

    const resultCount = useMemo(() => {
        if (sortedItems) return sortedItems.length;
        return 0;
    }, [sortedItems]);

    async function handleFormSubmit(event) {
        event.preventDefault();
        await saveFormData();
        await saveFormData(formData.station[0].radio_id);
        const custom = (await customPlaylistAutopilotRadios).data.includes(formData.station[0]?.radio_id) ? "custom/" : "";
        const url = `/playlist_autopilot/${custom}${formData.station[0]?.radio_id}?data_type=${formData.dataType}`;
        await fetchData(url);
        setCustomResults(!!custom);
    }

    async function handleCategoryChange(newCategory, itemId) {
        const orignalData = { ...data };
        try {
            setData(prev => ({
                ...prev,
                data: prev.data.map(item => (item.song.item_id === itemId ? { ...item, song_category_manual_move: newCategory } : item)),
            }));
            await axiosInstance.patch(`playlist_autopilot/custom/${data.radio_id}/${itemId}/${encodeURIComponent(newCategory)}?data_type=${data.data_type}`);
        } catch (error) {
            setData(orignalData);
        }
    }

    const columns = useMemo(() => {
        if (customResults) {
            return [
                { header: "Title", value: x => x.song.title },
                { header: "Artist", value: x => x.song.artist },
                { header: "Client ID", value: x => x.song.song_id },
                { header: "RA ID", value: x => x.song.item_id },
                { header: "Category Move", value: x => x.song_category_manual_move ?? x.song_category_move },
                { header: "Score Current", value: x => x.song_score },
                { header: "Score Long-Term", value: x => x.song_score_month },
                { header: "Burn Week", value: x => x.burn },
                { header: "Exposure", value: x => x.exposure },
            ];
        }

        return [
            { header: "Category Name", value: x => x.song_category },
            { header: "Category Move", value: x => x.song_category_move },
            { header: "Artist", value: x => x.song.artist },
            { header: "Title", value: x => x.song.title },
            { header: "Release Year", value: x => x.song.release_year },
            { header: "Spins", value: x => x.spins },
            { header: "Total Spins", value: x => x.total_spins },
            { header: "Score Week", value: x => x.song_score },
            { header: "Score Month", value: x => x.song_score_month },
            { header: "Burn", value: x => x.burn },
            { header: "Exposure", value: x => x.exposure },
        ];
    }, [customResults]);

    const csvHeaders = useMemo(() => columns.map(c => c.header), [columns]);
    const csvData = useMemo(() => sortedItems?.map(x => columns.map(c => c.value(x))), [sortedItems, columns]);

    const virtualizer = useWindowVirtualizer({
        count: sortedItems?.length || 1,
        estimateSize: useCallback(() => 54, []),
        getItemKey: useCallback(i => sortedItems?.length && sortedItems[i].song.item_id, [sortedItems]),
        overscan: 20,
        scrollMargin: tableContainerRef.current?.offsetTop ?? 0,
    });

    return (
        <>
            <ModulePage>
                <ModulePage.Title className="d-flex flex-column flex-md-row justify-content-between col-12">
                    <div className="text-start">Playlist Autopilot</div>
                    <Button
                        variant="ra-yellow"
                        className="d-flex align-self-md-center justify-content-center mt-2 mt-md-0 col-6 col-md-auto align-items-center column-gap-1"
                        onClick={() => setShowImportModal(true)}>
                        <Upload size={20} />
                        Import Playlist
                    </Button>
                </ModulePage.Title>
                <ModulePage.InputSelection handleFormSubmit={handleFormSubmit}>
                    <InputSelection.Main>
                        {!stationsAvailable && (
                            <p>
                                Contact your RA rep or{" "}
                                <a
                                    className="text-dark fw-bold"
                                    href="mailto:training@radioanalyzer.com?subject=Upgrade%20to%20Playlist%20Autopilot"
                                    target="_blank">
                                    Click Here
                                </a>{" "}
                                to upgrade...
                            </p>
                        )}
                        <StationPicker
                            formData={formData}
                            setFormData={setFormData}
                            size="md"
                            name={moduleName}
                            filterFunction={x => x.mtt_only}
                            moduleFilterFunction={x => x.module_id === 15}
                        />

                        <Form.Group as={Col} className="d-flex flex-column mb-3 mb-xl-0 col-12 col-xl-2">
                            <Form.Label>Data type</Form.Label>
                            <ToggleButtonGroup type={"radio"} name="demos" value={demos} className="ra-switcher btn-group">
                                <ToggleButton
                                    id={`plap-data-type-stream `}
                                    variant={"ra-dark-blue-outline"}
                                    disabled={false}
                                    value={1}
                                    onChange={() => setDemos(1)}>
                                    Stream
                                </ToggleButton>
                                <ToggleButton id={`plap-data-type-ppm`} variant={"ra-dark-blue-outline"} value={2} disabled={true} onChange={() => setDemos(2)}>
                                    Ratings
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Form.Group>
                    </InputSelection.Main>
                </ModulePage.InputSelection>
                <ModulePage.DataContent loading={loading} errorCode={errorCode}>
                    <DataContent.Placeholder show={!dataMemo}>
                        <PageDesciption
                            title={"Playlist Autopilot"}
                            whatList={[
                                "Playlist meeting prep in seconds, based on your categories, rotations, and guidelines",
                                "Optimal category moves are displayed based on song score – you only need to approve or discard the changes",
                                "Automated export to your music planning system coming soon!",
                            ]}
                            knowList={[
                                { text: "One time setup (we can help with this) is required for optimal results" },
                                { text: "Choose between your playlist, your back pool, or any category(ies) of your choosing" },
                                { text: "Click on trend to see the recent song development of spins and scores" },
                            ]}
                            relatedLinks={[
                                {
                                    title: "Introducing Playlist Autopilot: The Future of Music Curation",
                                    url: "https://radioanalyzer.com/playlist-autopilot/",
                                },
                                {
                                    title: "Playlist Autopilot: We Are Flicking the Switch to 'On'",
                                    url: "https://radioanalyzer.com/playlist-autopilot-update/",
                                },
                            ]}
                            videoSrc="https://www.youtube.com/embed/XNhTC6nnYSw?si=kqyWYKJJJmupIAW1"
                        />
                    </DataContent.Placeholder>
                    {data && (
                        <>
                            <SoleDescriptionRow>
                                <Stack direction="horizontal" className="flex-wrap column-gap-2">
                                    <Stack direction="horizontal" gap={1}>
                                        <span className=" text-nowrap">Station:</span>
                                        <Suspense>
                                            <Await resolve={competitorRadios}>
                                                {resolvedRadios => <span className="fw-bold">{getRadioName(data?.radio_id, resolvedRadios.data)}</span>}
                                            </Await>
                                        </Suspense>
                                    </Stack>
                                    <div className="vr d-none d-xl-inline-block"></div>
                                    {customResults ? (
                                        <Stack direction="horizontal" gap={1}>
                                            <span>Generated date:</span>
                                            <span className="fw-bold">{moment(data?.generated_at).format("L LT")}</span>
                                        </Stack>
                                    ) : (
                                        <Stack direction="horizontal" gap={1}>
                                            <span>Time period:</span>
                                            <span className="fw-bold">
                                                {moment(data.basisWeek).format("L")} - {moment(data.basisWeek).endOf("isoWeek").format("L")}
                                            </span>
                                        </Stack>
                                    )}
                                    <div className="vr d-none d-xl-inline-block"></div>{" "}
                                    <Stack direction="horizontal" gap={1}>
                                        <span>Time Frame:</span>
                                        <span className="fw-bold">
                                            {data.time_frame === 0 ? "Last Week" : data.time_frame === 1 ? "Last Month" : "Last Quarter"}
                                        </span>
                                    </Stack>
                                    <div className="vr d-none d-xl-inline-block"></div>
                                    <Stack direction="horizontal" gap={1}>
                                        <span>Category:</span>
                                        <span className="fw-bold">
                                            {mapPlaylistPerspective(perspectivesWithCategories, filterData.categories)?.perspective ?? "Custom"}
                                        </span>
                                    </Stack>
                                    <div className="vr d-none d-xl-inline-block"></div>{" "}
                                    <Stack direction="horizontal" gap={1}>
                                        <span>Results: </span>
                                        <span className="fw-bold">{resultCount}</span>
                                    </Stack>
                                </Stack>
                            </SoleDescriptionRow>
                            <Stack direction="horizontal" gap={3} className=" mb-2 flex-wrap row-gap-1 ">
                                <Form.Group as={Col} controlId={"playlist-autopilot-perspective"} className="col-12 col-xl-1 min-w-perspective mt-2">
                                    <Form.Label>Perspective</Form.Label>
                                    <Form.Select
                                        value={
                                            perspectivesWithCategories.find(
                                                x => JSON.stringify(x.categories.map(cg => cg.code)) === filterData.playlistPerspective,
                                            )
                                                ? filterData.playlistPerspective
                                                : "custom"
                                        }
                                        onChange={e => setFilterData({ ...filterData, playlistPerspective: e.target.value })}>
                                        {perspectivesWithCategories.map(x => (
                                            <option key={x.perspective} value={JSON.stringify(x.categories.map(cg => cg.code))}>
                                                {x.perspective}
                                            </option>
                                        ))}
                                        <option value={"custom"}>Custom</option>
                                    </Form.Select>
                                </Form.Group>
                                <SongCategoriesPicker
                                    formData={filterData}
                                    setFormData={setFilterData}
                                    options={perspectivesWithCategories[0]?.categories}
                                    name={moduleName}
                                />
                                <Form.Group as={Col} className="d-flex flex-column col-12 col-xl-auto">
                                    <Form.Label>
                                        Show Categories
                                        <RATooltip>
                                            Selecting 'Changes' will only display results with song categories that are suggested to be moved. Choosing 'All'
                                            will show categories that should be changed and those that should remain the same.
                                        </RATooltip>
                                    </Form.Label>
                                    <ToggleButtonGroup
                                        type="radio"
                                        name="categories"
                                        value={filterData.showMoves}
                                        onChange={value => setFilterData({ ...filterData, showMoves: value })}
                                        className="ra-switcher">
                                        <ToggleButton id="playlist-autopilot-plap-changes-only" variant="outline-secondary" value={"changes"}>
                                            Changes
                                        </ToggleButton>
                                        <ToggleButton id="playlist-autopilot-plap-changes-all" variant="outline-secondary" value={"all"}>
                                            All
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </Form.Group>
                            </Stack>
                            <Suspense>
                                <Await resolve={competitorRadios}>
                                    {resolvedRadios => (
                                        <SearchWithDownloadRow
                                            filterData={filterData}
                                            setFilterData={setFilterData}
                                            csvHeaders={csvHeaders}
                                            data={csvData}
                                            filename={`PLAP - ${data?.basis_week} - ${getRadioName(data?.radio_id, resolvedRadios.data)}`}
                                        />
                                    )}
                                </Await>
                            </Suspense>
                            <div className=" overflow-x-auto">
                                <PlaylistAutopilotHeader sortConfig={sortConfig} requestSort={requestSort} />
                                <VirtualizedWindowContainer className="playlist-autopilot-mw" containerRef={tableContainerRef} virtualizer={virtualizer}>
                                    {virtualizer.getVirtualItems().map(virtualRow => {
                                        const item = sortedItems[virtualRow.index];
                                        if (!item) return <EmptyTableRow key={virtualRow.key} text="No songs" />;
                                        return (
                                            <PlaylistAutopilotRow
                                                key={virtualRow.key}
                                                item={item}
                                                onChange={customResults && handleCategoryChange}
                                                virtualizer={virtualizer}
                                                virtualRow={virtualRow}
                                                setTrendSong={setTrendSong}
                                                radioId={customResults ? data.radio_id : null}
                                            />
                                        );
                                    })}
                                </VirtualizedWindowContainer>
                            </div>
                        </>
                    )}
                    <PlaylistAutopilotImportModal show={showImportModal} onClose={() => setShowImportModal(false)} />
                    <MusicTestTrendModalComponent radioId={data?.radio_id} dataType={data?.data_type} />
                </ModulePage.DataContent>
            </ModulePage>
        </>
    );
};

export default PlaylistAutopilot;
